import { roleFor } from "@netmountains/shared/browser";
import PageLoading from "components/PageLoading";
import { DASHBOARD_PAGES, landingRouteFor, preloadPages } from "layouts/dashboard/pages";
import RequireRole from "layouts/dashboard/RequireRole";
import { Suspense, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useRoutes, Navigate } from "react-router";
import lazyPage from "utils/lazyPage";

import { authService } from "./services/authService";

// Every layout and page loads on first use, each as a chunk of its own: the login doesn't load
// the dashboard, and a company account never loads the back office. The dashboard's pages, with
// the roles that may open them, are in layouts/dashboard/pages.js.
const DashboardLayout = lazyPage(() => import("./layouts/dashboard"));
const Datenschutz = lazyPage(() => import("./pages/Datenschutz"));
const ForgotPW = lazyPage(() => import("./pages/ForgotPW"));
const LoginPage = lazyPage(() => import("./pages/SignIn"));
const LogoutPage = lazyPage(() => import("./pages/logout"));
const Page404 = lazyPage(() => import("./pages/Page404"));
const PasswordReset = lazyPage(() => import("./pages/passwordReset"));
const ShowFile = lazyPage(() => import("./pages/showFile"));
const SignUp = lazyPage(() => import("./pages/SignUp"));
const ValidateEmail = lazyPage(() => import("./pages/validateEmail"));
// -----------------------------------------------------------------

export default function Router() {
  const account = useSelector(state => state.account);
  const dispatch = useDispatch();
  const signedIn = Boolean(account) && Object.keys(account).length > 0;
  const role = roleFor(account);

  // Sync Redux with user state on component mount
  // This ensures the Redux store is correctly populated after page reloads
  useEffect(() => {
    // Only try to sync if we're not already authenticated in Redux
    if (!account || Object.keys(account).length === 0) {
      authService.getCurrentUser(dispatch);
    }
  }, [dispatch, account]);

  // Loads the signed-in account's pages in the background, so they open without waiting, and
  // still open once the connection drops or a deploy replaced this build's chunks
  useEffect(() => {
    if (signedIn) preloadPages({ role });
  }, [signedIn, role]);

  const routes = useRoutes([
    {
      path: "/",
      element: <LoginPage />,
    },
    {
      path: "/reset",
      element: <LogoutPage />,
    },
    {
      path: "/register",
      element: <SignUp />,
    },
    {
      path: "/forgottenPW",
      element: <ForgotPW />,
    },
    { path: "/showFile", element: <ShowFile /> },
    {
      path: "/validate/:token",
      element: <ValidateEmail />,
    },
    {
      path: "/reset-password/:token",
      element: <PasswordReset />,
    },
    {
      path: "/datenschutz",
      element: <Datenschutz />,
    },
    {
      path: "/dashboard",
      element: <DashboardLayout />,
      children: [
        {
          element: <Navigate to={landingRouteFor(account)} />,
          index: true,
        },
        ...Object.entries(DASHBOARD_PAGES).map(([path, { roles, Page }]) => ({
          path,
          element: (
            <RequireRole roles={roles}>
              {/* Inside the layout, so the menu stays while a page loads */}
              <Suspense fallback={<PageLoading />}>
                <Page />
              </Suspense>
            </RequireRole>
          ),
        })),
      ],
    },
    {
      path: "/404",
      element: <Page404 />,
    },
    {
      path: "*",
      element: <Navigate to="/404" replace />,
    },
  ]);

  return <Suspense fallback={<PageLoading />}>{routes}</Suspense>;
}
